import React, { useEffect, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { DASHBOARD_TIME_RANGES } from '../../utils/timeRanges'
import { LegendDot, surface } from '../ui/surface'
import RangeSwitch, { schoolTimeRanges } from './RangeSwitch'

const palette = {
  inbound: { rgb: '110, 88, 255', legend: 'bg-brand-500' },
  outbound: { rgb: '64, 230, 255', legend: 'bg-accent-cyan' },
}

const verticalFade = (rgb) => ({ chart }) => {
  const { ctx, chartArea } = chart
  if (!chartArea) return `rgba(${rgb}, 0)`
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, `rgba(${rgb}, 0.28)`)
  gradient.addColorStop(1, `rgba(${rgb}, 0)`)
  return gradient
}

const getChartOptions = (t, isMobile = false) => ({
  responsive: true,
  maintainAspectRatio: false,
  spanGaps: false, // Не соединять линией через null значения
  interaction: {
    intersect: false,
    mode: 'index',
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      backgroundColor: '#181e2e',
      titleColor: '#f8fafc',
      bodyColor: '#cbd5f5',
      borderWidth: 0,
      displayColors: true,
      usePointStyle: true,
      boxWidth: 8,
      boxHeight: 8,
      padding: 12,
      cornerRadius: 12,
      filter: (tooltipItem) => {
        // Не показывать tooltip для null значений
        return tooltipItem.raw?.y !== null
      },
      callbacks: {
        title: (tooltipItems) => {
          const item = tooltipItems?.[0]
          if (!item) return ''
          // Используем raw.x для получения временной метки из исходных данных
          const timestamp = item.raw?.x || item.parsed?.x
          if (!timestamp || isNaN(timestamp)) return ''
          const date = new Date(timestamp)
          // Проверяем, что дата валидна
          if (isNaN(date.getTime())) return ''
          return date.toLocaleString('ru-KZ', {
            timeZone: 'Asia/Almaty',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        },
        label: (context) => {
          // Не показывать метку для null значений
          if (context.raw?.y === null) return null
          const label = context.dataset?.label || ''
          const value = context.parsed.y
          return `${label}: ${Number(value).toFixed(2)} ${t('trafficOverview.mbps')}`
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: {
        color: '#64748b',
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: isMobile ? 6 : 10,
        font: {
          family: 'Inter',
          size: isMobile ? 9 : 11,
        },
      },
    },
    y: {
      grid: { color: 'rgba(148, 163, 184, 0.06)' },
      border: { display: false },
      ticks: {
        color: '#64748b',
        padding: isMobile ? 4 : 8,
        maxTicksLimit: 6,
        font: {
          family: 'Inter',
          size: isMobile ? 9 : 11,
        },
      },
      beginAtZero: true,
    },
  },
  layout: {
    padding: { top: 8 },
  },
})

const buildChartData = (series, t, timeRange) => {
  // Получаем конфигурацию временного диапазона
  const rangeConfig = DASHBOARD_TIME_RANGES[timeRange]
  if (!rangeConfig) {
    return null
  }

  const { duration, step } = rangeConfig
  const now = Math.floor(Date.now() / 1000)
  const end = now
  const start = end - duration

  // Создаем Map для быстрого поиска данных по timestamp
  const inDataMap = new Map()
  const outDataMap = new Map()

  if (series?.in?.length) {
    series.in.forEach((point) => {
      inDataMap.set(point[0], parseFloat(point[1]))
    })
  }

  if (series?.out?.length) {
    series.out.forEach((point) => {
      outDataMap.set(point[0], parseFloat(point[1]))
    })
  }

  // Генерируем полный диапазон временных точек
  const timestamps = []
  for (let ts = start; ts <= end; ts += step) {
    timestamps.push(ts)
  }

  // Функция для поиска ближайшего значения (с допуском в 1 step)
  const findValue = (dataMap, targetTs) => {
    if (dataMap.has(targetTs)) {
      return dataMap.get(targetTs)
    }
    const tolerance = step / 2
    for (const [ts, value] of dataMap.entries()) {
      if (Math.abs(ts - targetTs) <= tolerance) {
        return value
      }
    }
    return null
  }

  // Создаем данные с временными метками
  const inData = timestamps.map((ts) => ({
    x: ts * 1000,
    y: findValue(inDataMap, ts),
  }))

  const outData = timestamps.map((ts) => ({
    x: ts * 1000,
    y: findValue(outDataMap, ts),
  }))

  // Проверяем, есть ли хоть какие-то данные
  const hasAnyData = inData.some((v) => v.y !== null) || outData.some((v) => v.y !== null)
  if (!hasAnyData) {
    return null
  }

  // Определяем формат метки в зависимости от диапазона времени
  const getDateFormatOptions = (range) => {
    if (range === '7d' || range === '30d') {
      return {
        timeZone: 'Asia/Almaty',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }
    }
    return {
      timeZone: 'Asia/Almaty',
      hour: '2-digit',
      minute: '2-digit',
    }
  }

  const formatOptions = getDateFormatOptions(timeRange)

  return {
    labels: timestamps.map((ts) =>
      new Date(ts * 1000).toLocaleString('ru-KZ', formatOptions),
    ),
    datasets: [
      buildDataset(t('trafficOverview.incomingTraffic'), inData, palette.inbound.rgb),
      buildDataset(t('trafficOverview.outgoingTraffic'), outData, palette.outbound.rgb),
    ],
  }
}

const buildDataset = (label, data, rgb) => ({
  label,
  data,
  borderColor: `rgb(${rgb})`,
  backgroundColor: verticalFade(rgb),
  fill: true,
  tension: 0.38,
  pointRadius: 0,
  pointHoverRadius: 5,
  pointHoverBorderWidth: 2,
  pointHoverBackgroundColor: `rgb(${rgb})`,
  pointHoverBorderColor: '#ffffff',
  borderWidth: 2,
  spanGaps: false,
})

const TrafficOverview = ({
  timeRange,
  onChangeTimeRange,
  series,
  isLoading,
  title,
  subtitle,
}) => {
  const { t } = useTranslation();
  const chartData = buildChartData(series, t, timeRange);
  const ranges = schoolTimeRanges(t);
  const displayTitle = title || t('trafficOverview.title');
  const displaySubtitle = subtitle || t('trafficOverview.subtitle');
  
  // Определяем, мобильное ли устройство
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section className={`flex h-full w-full flex-col rounded-3xl ${surface.section} p-4 sm:p-6`}>
      <header className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h3 className="font-display text-lg text-white sm:text-xl">{displayTitle}</h3>
          <p className="mt-1 text-sm text-muted-400">{displaySubtitle}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            <LegendDot color={palette.inbound.legend}>{t('trafficOverview.incomingTraffic')}</LegendDot>
            <LegendDot color={palette.outbound.legend}>{t('trafficOverview.outgoingTraffic')}</LegendDot>
            <span className="text-xs text-muted-500">{t('trafficOverview.mbps')}</span>
          </div>
        </div>
        <RangeSwitch ranges={ranges} value={timeRange} onChange={onChangeTimeRange} />
      </header>

      <div className="relative min-h-[240px] w-full flex-1 sm:min-h-[280px] md:min-h-[320px] lg:min-h-[360px] xl:min-h-[480px]">
        {isLoading ? (
          <div className="flex h-full items-center justify-center gap-3 text-sm text-muted-400">
            <span className="material-icons animate-spin text-brand-300">progress_activity</span>
            {t('trafficOverview.loadingData')}
          </div>
        ) : chartData ? (
          <Line data={chartData} options={getChartOptions(t, isMobile)} />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-500">
            {t('trafficOverview.noDataForPeriod')}
          </div>
        )}
      </div>
    </section>
  )
}

export default TrafficOverview

